import React from "react";
import Box from "@mui/material/Box";
import Divider from "@mui/material/Divider";
import Typography from "@mui/material/Typography";
import CircularProgress from "@mui/material/CircularProgress";
import AssetImage from "src/components/wizard/Item/AssetImage";
import { getCustomDescriptionValue } from "src/utils/asset";

const styles = {
  itemContainer: {
    cursor: "pointer",
    borderRadius: 1,
    "&:hover": { bgcolor: "rgba(0, 0, 0, 0.04)" },
  },
  itemContainerDisabled: {
    pointerEvents: "none",
    opacity: 0.7,
  },
  description: {
    overflow: "hidden",
    textOverflow: "ellipsis",
    display: "-webkit-box",
    WebkitLineClamp: 2,
    WebkitBoxOrient: "vertical",
  },
  locationText: {
    fontSize: 14,
    color: "text.secondary",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
};

const getLocationInfo = rackLocations => {
  if (!rackLocations?.length) {
    return { mobileLocation: null, rackLocation: null };
  }

  const firstLocation = rackLocations[0];
  return {
    mobileLocation: firstLocation.mobile_warehouse_location?.location_name || null,
    rackLocation: firstLocation.warehouse_locations?.[0]?.location_name || null,
  };
};

const getDescription = asset => {
  if (asset?.description) {
    return asset.description;
  }

  return getCustomDescriptionValue(asset);
};

const getLocationDisplay = ({ mobileLocation, rackLocation }) => {
  if (mobileLocation && rackLocation) {
    return (
      <>
        <Typography sx={styles.locationText} title={mobileLocation}>
          {mobileLocation}
        </Typography>
        <Typography sx={styles.locationText}>-</Typography>
        <Typography sx={styles.locationText} title={rackLocation}>
          {rackLocation}
        </Typography>
      </>
    );
  }

  if (mobileLocation) {
    return (
      <Typography sx={styles.locationText} title={mobileLocation}>
        {mobileLocation}
      </Typography>
    );
  }

  if (rackLocation) {
    return (
      <Typography sx={styles.locationText} title={rackLocation}>
        {rackLocation}
      </Typography>
    );
  }

  return <Typography sx={styles.locationText}>No location data</Typography>;
};

export default function SearchResultsView({ assets, onSelect, selectingAssetId }) {
  if (!assets?.length) {
    return (
      <Box mt={2} textAlign="center">
        <Typography color="text.secondary">No inventory found for the current search.</Typography>
      </Box>
    );
  }

  return (
    <Box mt={1}>
      {assets.map((asset, index) => {
        const { mobileLocation, rackLocation } = getLocationInfo(asset.rackLocations);
        const isSelecting = selectingAssetId === asset.id;
        const hasPendingSelection = Boolean(selectingAssetId);
        const key = asset.id || `${asset.itemId}-${index}`;

        return (
          <React.Fragment key={key}>
            {index > 0 && <Divider sx={{ my: 1.5 }} />}
            <Box
              display="flex"
              p={1}
              gap={2}
              sx={{
                ...styles.itemContainer,
                ...(hasPendingSelection && styles.itemContainerDisabled),
              }}
              onClick={() => onSelect(asset)}
            >
              <AssetImage width={100} height={90} image={asset?.images?.[0]?.url} />
              <Box
                flex={1}
                display="flex"
                flexDirection={{ xs: "column", sm: "row" }}
                gap={{ xs: 1, sm: 2 }}
              >
                <Box flex={1}>
                  <Box display="flex" alignItems="center" gap={0.5} mb="0.35em">
                    <Typography variant="body1" fontWeight={500}>
                      {asset.itemId}
                    </Typography>
                  </Box>
                  <Typography variant="body1" gutterBottom sx={styles.description}>
                    {getDescription(asset)}
                  </Typography>
                  <Typography variant="body1">Quantity: {asset.availableQuantity || 0}</Typography>
                </Box>
                <Box
                  display="flex"
                  flexDirection="column"
                  justifyContent="center"
                  alignItems={{ xs: "flex-start", sm: "center" }}
                  minWidth={{ xs: "auto", sm: 150 }}
                  gap={0.25}
                >
                  {isSelecting ? (
                    <CircularProgress size={24} />
                  ) : (
                    getLocationDisplay({ mobileLocation, rackLocation })
                  )}
                </Box>
              </Box>
            </Box>
          </React.Fragment>
        );
      })}
    </Box>
  );
}
